import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { User, Package, Award, Settings, MapPin, ChevronRight, LogOut, Camera, Heart, ShoppingBag, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useFavorites } from '../context/FavoritesContext';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import './Account.css';

const Account = () => {
    const { user, logout } = useAuth();
    const { favorites, toggleFavorite } = useFavorites();
    const { addToCart } = useCart();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('orders');

    if (!user) {
        navigate('/');
        return null;
    }

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    // Mock order history
    const orders = [
        { id: '#4829', date: '12 Jan 2026', status: 'Livré', total: '32.50€', items: '2x Bacon Burger, 1x Large Fries' },
        { id: '#4712', date: '05 Jan 2026', status: 'Livré', total: '18.90€', items: '1x Veggie Tower, 1x Cola' },
        { id: '#4601', date: '28 Dec 2025', status: 'Annulé', total: '24.00€', items: '1x BBQ Special, 2x Cookies' },
    ];

    const tabs = [
        { id: 'orders', label: 'Commandes', icon: <Package size={20} /> },
        { id: 'favorites', label: 'Favoris', icon: <Heart size={20} /> },
        { id: 'loyalty', label: 'Fidélité', icon: <Award size={20} /> },
        { id: 'settings', label: 'Paramètres', icon: <Settings size={20} /> }
    ];

    return (
        <div className="account-page">
            <div className="account-container">
                {/* Profile Header */}
                <section className="profile-header">
                    <div className="profile-info-main">
                        <div className="avatar-wrapper">
                            <div className="profile-avatar-large">
                                {user.name.charAt(0)}
                            </div>
                            <button className="change-avatar-btn">
                                <Camera size={14} />
                            </button>
                        </div>
                        <div className="user-text">
                            <h1 className="user-name">{user.name}</h1>
                            <p className="user-meta">{user.email} • Membre depuis Janvier 2026</p>
                        </div>
                    </div>
                    <button onClick={handleLogout} className="logout-action-btn">
                        <LogOut size={18} />
                        <span>Déconnexion</span>
                    </button>
                </section>

                <div className="account-content-grid">
                    {/* Sidebar Nav */}
                    <aside className="account-sidebar">
                        <div className="loyalty-snapshot">
                            <div className="loyalty-icon-box">
                                <Award size={24} />
                            </div>
                            <div className="loyalty-data">
                                <span className="points-count">{user.points}</span>
                                <span className="points-label">Points Sugar Hut</span>
                            </div>
                        </div>

                        <nav className="account-nav">
                            {tabs.map(tab => (
                                <button
                                    key={tab.id}
                                    className={`nav-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                                    onClick={() => setActiveTab(tab.id)}
                                >
                                    {tab.icon}
                                    <span>{tab.label}</span>
                                    {activeTab === tab.id && <motion.div layoutId="tab-active" className="active-glow" />}
                                </button>
                            ))}
                        </nav>
                    </aside>

                    {/* Main Content Area */}
                    <main className="account-main-area">
                        {activeTab === 'orders' && (
                            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="content-section">
                                <h2 className="section-title">Historique des commandes</h2>
                                <div className="orders-list">
                                    {orders.map(order => (
                                        <div key={order.id} className="order-card-row">
                                            <div className="order-info">
                                                <span className="order-number">{order.id}</span>
                                                <span className="order-date">{order.date}</span>
                                            </div>
                                            <div className="order-items-summary">
                                                <p>{order.items}</p>
                                            </div>
                                            <div className="order-status-wrap">
                                                <span className={`status-pill ${order.status.toLowerCase()}`}>
                                                    {order.status}
                                                </span>
                                                <span className="order-price">{order.total}</span>
                                            </div>
                                            <button className="order-detail-btn">
                                                <ChevronRight size={20} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>
                        )}

                        {activeTab === 'favorites' && (
                            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="content-section">
                                <h2 className="section-title">Mes Favoris</h2>
                                {favorites.length === 0 ? (
                                    <div className="empty-account-state">
                                        <Heart size={48} className="text-gray-200 mb-4" />
                                        <p>Vous n'avez pas encore de favoris.</p>
                                    </div>
                                ) : (
                                    <div className="fav-account-grid">
                                        {favorites.map(item => (
                                            <div key={item.id} className="fav-account-card">
                                                <div className="fav-card-info">
                                                    <h3 className="font-bold">{item.name}</h3>
                                                    <span className="text-primary font-black">{item.price}€</span>
                                                </div>
                                                <div className="fav-card-actions">
                                                    <button
                                                        className="add-to-cart-small"
                                                        onClick={() => addToCart(item)}
                                                    >
                                                        <ShoppingBag size={18} />
                                                    </button>
                                                    <button
                                                        className="remove-fav-account"
                                                        onClick={() => toggleFavorite(item)}
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </motion.div>
                        )}

                        {activeTab === 'loyalty' && (
                            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="content-section">
                                <h2 className="section-title">Ma Fidélité</h2>
                                <div className="loyalty-card-premium">
                                    <div className="loyalty-top">
                                        <div className="loyalty-badge">Membre Explorer</div>
                                        <div className="loyalty-pts">{user.points} pts</div>
                                    </div>
                                    <div className="loyalty-progress-box">
                                        <div className="progress-text">
                                            <span>Prochain palier : 100 pts (Menu offert)</span>
                                            <span>{Math.floor((user.points / 100) * 100)}%</span>
                                        </div>
                                        <div className="progress-bar-bg">
                                            <motion.div
                                                className="progress-bar-fill"
                                                initial={{ width: 0 }}
                                                animate={{ width: `${(user.points / 100) * 100}%` }}
                                            />
                                        </div>
                                    </div>
                                    <div className="loyalty-perks">
                                        <div className="perk">Frites offertes dès 50 points</div>
                                        <div className="perk">Accès prioritaire aux nouveautés</div>
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {activeTab === 'settings' && (
                            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="content-section">
                                <h2 className="section-title">Paramètres du compte</h2>
                                <div className="settings-grid">
                                    <div className="settings-group">
                                        <label>Nom complet</label>
                                        <input type="text" defaultValue={user.name} className="settings-input" />
                                    </div>
                                    <div className="settings-group">
                                        <label>Adresse Email</label>
                                        <input type="email" defaultValue={user.email} className="settings-input" />
                                    </div>
                                    <div className="settings-group">
                                        <label>Téléphone</label>
                                        <input type="tel" placeholder="06 .. .. .. .." className="settings-input" />
                                    </div>
                                    <div className="settings-group">
                                        <label>Adresse de livraison par défaut</label>
                                        <div className="address-picker">
                                            <MapPin size={18} />
                                            <input type="text" placeholder="Ajouter une adresse..." className="settings-input no-border" />
                                        </div>
                                    </div>
                                </div>
                                <button className="save-settings-btn">Enregistrer les modifications</button>
                            </motion.div>
                        )}
                    </main>
                </div>
            </div>
        </div>
    );
};

export default Account;
